import React, { useState } from 'react';
import { Target, Plus, Trash2, Edit2, Save, DollarSign, Calendar, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Goal {
  id: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  deadline: string;
  category: 'short-term' | 'medium-term' | 'long-term';
}

interface FinancialGoalsProps {
  monthlyIncome: number;
  monthlySavings: number;
}

export const FinancialGoals: React.FC<FinancialGoalsProps> = ({
  monthlyIncome,
  monthlySavings
}) => {
  const [goals, setGoals] = useState<Goal[]>(() => {
    const savedGoals = localStorage.getItem('financialGoals');
    return savedGoals ? JSON.parse(savedGoals) : [];
  });
  const [isAddingGoal, setIsAddingGoal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<string | null>(null);
  const [newGoal, setNewGoal] = useState<Omit<Goal, 'id'>>({
    name: '',
    targetAmount: 0,
    currentAmount: 0,
    deadline: '',
    category: 'short-term'
  });

  const saveGoals = (updatedGoals: Goal[]) => {
    localStorage.setItem('financialGoals', JSON.stringify(updatedGoals));
    setGoals(updatedGoals);
  };

  const addGoal = () => {
    if (!newGoal.name || !newGoal.targetAmount || !newGoal.deadline) return;

    const goal: Goal = {
      ...newGoal,
      id: Math.random().toString(36).substr(2, 9)
    };

    saveGoals([...goals, goal]);
    setIsAddingGoal(false);
    setNewGoal({
      name: '',
      targetAmount: 0,
      currentAmount: 0,
      deadline: '',
      category: 'short-term'
    });
  };

  const updateGoal = (id: string, updates: Partial<Goal>) => {
    const updatedGoals = goals.map(goal =>
      goal.id === id ? { ...goal, ...updates } : goal
    );
    saveGoals(updatedGoals);
  };

  const deleteGoal = (id: string) => {
    saveGoals(goals.filter(goal => goal.id !== id));
  };

  const calculateProgress = (current: number, target: number) => {
    return (current / target) * 100;
  };

  const getMonthsToGoal = (deadline: string) => {
    const months = Math.ceil(
      (new Date(deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24 * 30)
    );
    return Math.max(0, months);
  };

  const calculateMonthlyRequired = (goal: Goal) => {
    const monthsLeft = getMonthsToGoal(goal.deadline);
    if (monthsLeft === 0) return 0;
    return (goal.targetAmount - goal.currentAmount) / monthsLeft;
  };

  const calculateGoalFeasibility = (goal: Goal) => {
    const monthlyRequired = calculateMonthlyRequired(goal);
    const currentSavingsRate = monthlySavings;
    const savingsDeficit = monthlyRequired - currentSavingsRate;
    const percentOfIncome = (monthlyRequired / monthlyIncome) * 100;

    if (monthlyRequired <= currentSavingsRate) {
      return {
        status: 'on-track',
        message: `You're on track! Current savings rate exceeds the required ${monthlyRequired.toLocaleString()} per month.`,
        icon: CheckCircle2,
        color: 'text-emerald-500'
      };
    } else if (percentOfIncome > 50) {
      return {
        status: 'unrealistic',
        message: `This goal requires ${percentOfIncome.toFixed(1)}% of your monthly income. Consider extending the deadline or adjusting the target.`,
        icon: XCircle,
        color: 'text-red-500'
      };
    } else {
      return {
        status: 'needs-adjustment',
        message: `You need to increase monthly savings by $${savingsDeficit.toLocaleString()} to reach this goal.`,
        icon: AlertCircle,
        color: 'text-amber-500'
      };
    }
  };

  const getTotalRequiredSavings = () => {
    return goals.reduce((total, goal) => total + calculateMonthlyRequired(goal), 0);
  };

  const getSavingsGap = () => {
    const totalRequired = getTotalRequiredSavings();
    return totalRequired - monthlySavings;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/10">
            <Target className="w-6 h-6 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-semibold">Financial Goals</h2>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsAddingGoal(true)}
          className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Goal
        </motion.button>
      </div>

      {/* Savings Overview */}
      <div className="glass-card p-6 border border-emerald-500/20">
        <h3 className="text-lg font-semibold mb-4">Savings Overview</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-slate-400 mb-1">Current Monthly Savings</p>
            <p className="text-2xl font-bold text-emerald-500">${monthlySavings.toLocaleString()}</p>
            <p className="text-sm text-slate-400">
              {((monthlySavings / monthlyIncome) * 100).toFixed(1)}% of income
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-400 mb-1">Required for Goals</p>
            <p className="text-2xl font-bold text-emerald-500">${getTotalRequiredSavings().toLocaleString()}</p>
            <p className="text-sm text-slate-400">
              {((getTotalRequiredSavings() / monthlyIncome) * 100).toFixed(1)}% of income
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-400 mb-1">Monthly Savings Gap</p>
            <p className={`text-2xl font-bold ${getSavingsGap() > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
              ${Math.abs(getSavingsGap()).toLocaleString()}
            </p>
            <p className="text-sm text-slate-400">
              {getSavingsGap() > 0 ? 'Shortfall' : 'Surplus'}
            </p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isAddingGoal && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <h3 className="text-lg font-semibold mb-4">New Financial Goal</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Goal Name</label>
                <input
                  type="text"
                  value={newGoal.name}
                  onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                  placeholder="e.g., Emergency Fund"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Target Amount</label>
                <div className="relative">
                  <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="number"
                    value={newGoal.targetAmount}
                    onChange={(e) => setNewGoal({ ...newGoal, targetAmount: Number(e.target.value) })}
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                    min="0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Current Amount</label>
                <div className="relative">
                  <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="number"
                    value={newGoal.currentAmount}
                    onChange={(e) => setNewGoal({ ...newGoal, currentAmount: Number(e.target.value) })}
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                    min="0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Target Date</label>
                <div className="relative">
                  <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="date"
                    value={newGoal.deadline}
                    onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Category</label>
                <select
                  value={newGoal.category}
                  onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value as Goal['category'] })}
                  className="glass-input px-4 py-2 w-full rounded-lg"
                >
                  <option value="short-term">Short Term (&lt; 1 year)</option>
                  <option value="medium-term">Medium Term (1-5 years)</option>
                  <option value="long-term">Long Term (&gt; 5 years)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsAddingGoal(false)}
                className="px-4 py-2 bg-slate-500/10 text-slate-300 rounded-lg hover:bg-slate-500/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addGoal}
                className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Goal
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid md:grid-cols-2 gap-6">
        {goals.map(goal => (
          <motion.div
            key={goal.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg">{goal.name}</h3>
                <span className="text-sm text-slate-400 px-2 py-0.5 bg-emerald-500/10 rounded-full">
                  {goal.category.replace('-', ' ')}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingGoal(goal.id === editingGoal ? null : goal.id)}
                  className="p-2 hover:bg-emerald-500/10 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4 text-emerald-500" />
                </button>
                <button
                  onClick={() => deleteGoal(goal.id)}
                  className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>

            {editingGoal === goal.id ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Current Amount</label>
                  <div className="relative">
                    <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="number"
                      value={goal.currentAmount}
                      onChange={(e) => updateGoal(goal.id, { currentAmount: Number(e.target.value) })}
                      className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                      min="0"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Target Date</label>
                  <div className="relative">
                    <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="date"
                      value={goal.deadline}
                      onChange={(e) => updateGoal(goal.id, { deadline: e.target.value })}
                      className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-baseline">
                  <div className="space-y-1">
                    <span className="text-sm text-slate-400">Current</span>
                    <p className="text-2xl font-bold">${goal.currentAmount.toLocaleString()}</p>
                  </div>
                  <div className="text-right space-y-1">
                    <span className="text-sm text-slate-400">Target</span>
                    <p className="text-2xl font-bold text-emerald-500">
                      ${goal.targetAmount.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, calculateProgress(goal.currentAmount, goal.targetAmount))}%` }}
                      className="h-full bg-emerald-500"
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-500">
                      {calculateProgress(goal.currentAmount, goal.targetAmount).toFixed(1)}% Complete
                    </span>
                    <span className="text-slate-400">
                      ${(goal.targetAmount - goal.currentAmount).toLocaleString()} to go
                    </span>
                  </div>
                </div>

                <div className="flex justify-between text-sm text-slate-400">
                  <span>Target Date: {new Date(goal.deadline).toLocaleDateString()}</span>
                  <span>{getMonthsToGoal(goal.deadline)} months left</span>
                </div>

                <div className="glass-card p-4 border border-emerald-500/20 space-y-2">
                  <p className="text-sm text-slate-400">Required Monthly Savings</p>
                  <div className="flex items-baseline justify-between">
                    <p className="text-xl font-bold">${calculateMonthlyRequired(goal).toLocaleString()}</p>
                    <p className="text-sm text-slate-400">
                      {((calculateMonthlyRequired(goal) / monthlyIncome) * 100).toFixed(1)}% of income
                    </p>
                  </div>
                  
                  {/* Goal Feasibility Analysis */}
                  {(() => {
                    const feasibility = calculateGoalFeasibility(goal);
                    const Icon = feasibility.icon;
                    return (
                      <div className={`flex items-center gap-2 text-sm ${feasibility.color}`}>
                        <Icon className="w-4 h-4" />
                        {feasibility.message}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {goals.length === 0 && !isAddingGoal && (
        <div className="text-center py-12 text-slate-400">
          <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No financial goals set yet. Add your first goal to start tracking!</p>
        </div>
      )}
    </div>
  );
};